/**
 * Shared utilities for weekly menu validation and management
 * Used by both WeeklyMenuSelector and PackageBuilder components
 */

class WeeklyMenuUtils {
  static SESSION_STORAGE_KEY = 'weeklyMenuSelection';

  /**
   * Parse a date string as EST timezone
   * The date string format is like "2025-10-17T13:00:27.140Z" but represents EST time, not UTC
   * This function converts the EST time to the correct UTC time that JavaScript Date uses internally
   */
  static parseESTDate(dateString) {
    if (!dateString) return null;

    if (!dateString.includes('T')) {
      dateString += 'T12:00:00';
    }

    // Remove the 'Z' suffix since it's not actually UTC
    const cleanDateString = dateString.replace(/Z$/, '');

    return new Date(cleanDateString);
  }

  /**
   * Get current weekly menu selection from session storage
   */
  static getSessionData() {
    try {
      const data = localStorage.getItem(this.SESSION_STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error reading weekly menu session storage:', error);
      return {};
    }
  }

  /**
   * Set weekly menu selection in session storage
   */
  static setSessionData(data) {
    try {
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error writing to weekly menu session storage:', error);
    }
  }

  /**
   * Set enriched weekly menu selection data including IDs and timestamps for cart properties
   */
  static setEnrichedSessionData(menu, deliveryDateInfo) {
    const data = {
      weeklyMenuId: menu.id,
      weeklyMenuHandle: menu.handle,
      weeklyMenuTitle: menu.title,
      weeklyMenuLabel: menu.label,
      weeklyMenuCollectionHandle: menu.collection_handle || null,
      deliveryDateId: deliveryDateInfo.id,
      deliveryDateLabel: this.formatDeliveryDate(deliveryDateInfo.date),
      deliveryDateHandle: deliveryDateInfo.handle,
      deliveryDate: deliveryDateInfo.date,
      deliveryDateTimestamp: Math.floor(new Date(this.parseESTDate(deliveryDateInfo.date)).getTime() / 1000),
      deliveryDateCutOffTimestamp: deliveryDateInfo.cutoff_date ? Math.floor(this.parseESTDate(deliveryDateInfo.cutoff_date).getTime() / 1000) : null,
      cutoffDate: deliveryDateInfo.cutoff_date
    };
    
    this.setSessionData(data);
  }

  /**
   * Get cart properties object for the current session data
   */
  static getCartProperties(additionalProperties = {}) {
    const sessionData = this.getSessionData();
    const timestamp = Math.floor(Date.now() / 1000);
    
    if (!sessionData.weeklyMenuId || !sessionData.deliveryDateId) {
      return {
        _addToCartTimestamp: timestamp.toString(),
        ...additionalProperties
      };
    }

    return {
      _addToCartTimestamp: timestamp.toString(),
      _weeklyMenuId: sessionData.weeklyMenuId || '',
      _weeklyMenuHandle: sessionData.weeklyMenuHandle || '',
      _weeklyMenuTitle: sessionData.weeklyMenuTitle || '',
      _weeklyMenuLabel: sessionData.weeklyMenuLabel || '',
      _deliveryDateId: sessionData.deliveryDateId || '',
      _deliveryDateLabel: sessionData.deliveryDateLabel || '',
      _deliveryDateHandle: sessionData.deliveryDateHandle || '',
      _deliveryDateTimestamp: sessionData.deliveryDateTimestamp ? sessionData.deliveryDateTimestamp.toString() : '',
      _deliveryDateCutOffTimestamp: sessionData.deliveryDateCutOffTimestamp ? sessionData.deliveryDateCutOffTimestamp.toString() : '',
      ...additionalProperties
    };
  }

  /**
   * Get cart properties object for specific menu and delivery date data
   */
  static getCartPropertiesForMenu(menu, deliveryDateInfo, additionalProperties = {}) {
    const timestamp = Math.floor(Date.now() / 1000);
    
    if (!menu || !deliveryDateInfo) {
      return {
        _addToCartTimestamp: timestamp.toString(),
        ...additionalProperties
      };
    }

    return {
      _addToCartTimestamp: timestamp.toString(),
      _weeklyMenuId: menu.id || '',
      _weeklyMenuHandle: menu.handle || menu.name || '',
      _weeklyMenuTitle: menu.title || '',
      _weeklyMenuLabel: menu.label || '',
      _deliveryDateId: deliveryDateInfo.id || '',
      _deliveryDateHandle: deliveryDateInfo.handle || '',
      _deliveryDateLabel: this.formatDeliveryDate(deliveryDateInfo.date),
      _deliveryDateTimestamp: Math.floor(new Date(this.parseESTDate(deliveryDateInfo.date)).getTime() / 1000).toString(),
      _deliveryDateCutOffTimestamp: deliveryDateInfo.cutoff_date ? Math.floor(this.parseESTDate(deliveryDateInfo.cutoff_date).getTime() / 1000).toString() : '',
      ...additionalProperties
    };
  }

  /**
   * Clear weekly menu selection from session storage
   */
  static clearSessionData() {
    try {
      localStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing weekly menu session storage:', error);
    }
  }

  /**
   * Filter weekly menus based on visibility dates
   */
  static getAvailableWeeklyMenus(weeklyMenus) {
    if (!weeklyMenus || !Array.isArray(weeklyMenus)) {
      return [];
    }

    const now = new Date();
    
    return weeklyMenus.filter(menu => {
      if (menu.visible_after_date) {
        const visibleAfter = this.parseESTDate(menu.visible_after_date);
        if (now < visibleAfter) {
          return false;
        }
      }

      if (menu.hidden_after_date) {
        const hiddenAfter = this.parseESTDate(menu.hidden_after_date);
        if (now >= hiddenAfter) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Check if a delivery date is still valid for a weekly menu
   */
  static isDeliveryDateValid(menu, deliveryDateString) {
    if (!menu || !menu.delivery_dates || !deliveryDateString) {
      return false;
    }

    return menu.delivery_dates.some(dateInfo => {
      return dateInfo.date === deliveryDateString && this.isDeliveryDateAvailable(dateInfo);
    });
  }

  /**
   * Check if a delivery date is available (not past cutoff, not in the past)
   */
  static isDeliveryDateAvailable(dateInfo) {
    const now = new Date();
    
    // Check if cutoff date has passed
    if (dateInfo.cutoff_date) {
      const cutoffDate = this.parseESTDate(dateInfo.cutoff_date);
      if (now >= cutoffDate) {
        return false;
      }
    }

    // Check if delivery date is in the future
    const deliveryDate = new Date(dateInfo.date);
    return deliveryDate > now;
  }

  /**
   * Check if current session data is still valid (cutoff date hasn't passed and delivery date is in the future)
   */
  static isSessionDataValid() {
    const sessionData = this.getSessionData();
    
    if (!sessionData.deliveryDate) {
      return false; // No delivery date stored
    }
    
    const now = new Date();
    
    // Check if delivery date is in the future
    const deliveryDate = new Date(sessionData.deliveryDate);
    if (deliveryDate <= now) {
      return false;
    }
    
    // Check if cutoff date hasn't passed (if cutoff date exists)
    if (sessionData.cutoffDate) {
      const cutoffDate = this.parseESTDate(sessionData.cutoffDate);
      if (now >= cutoffDate) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if a product is exempt from weekly menu requirements
   */
  static isProductWeeklyMenuExempt(productData) {
    return productData && productData.product && productData.product.weekly_menu_exempt === 1;
  }

  /**
   * Validate if a product can be added to cart with current weekly menu selection
   * Returns an object with validation result and actions needed
   */
  static async validateWeeklyMenuCompatibility(productData) {
    const sessionData = this.getSessionData();

    // Check if product is exempt from weekly menu requirements
    if (this.isProductWeeklyMenuExempt(productData)) {
      return {
        canProceed: true,
        action: 'proceed',
        message: 'Product is exempt from weekly menu requirements'
      };
    }

    const productWeeklyMenus = productData && productData.weeklyMenus ? productData.weeklyMenus : null;

    // If product has no weekly menus and is not exempt, it cannot be added
    if (!productWeeklyMenus || productWeeklyMenus.length === 0) {
      return {
        canProceed: false,
        action: 'noMenusAvailable',
        message: 'Product has no weekly menus'
      };
    }

    const availableMenus = this.getAvailableWeeklyMenus(productWeeklyMenus);

    if (availableMenus.length === 0) {
      return {
        canProceed: false,
        action: 'noMenusAvailable',
        availableMenus: [],
        message: 'No available weekly menus for this product'
      };
    }

    // Check if cutoff date has passed or delivery date is in the past for current session data
    if (sessionData.weeklyMenuTitle && sessionData.deliveryDate) {
      const now = new Date();
      
      // Check if delivery date is in the past
      const deliveryDate = new Date(sessionData.deliveryDate);
      if (deliveryDate <= now) {
        // Delivery date has passed, clear session data and require re-selection
        this.clearSessionData();
        return {
          canProceed: false,
          action: 'selectMenu',
          availableMenus,
          message: 'Delivery date has passed. Please select a new weekly menu and delivery date'
        };
      }
      
      // Check if cutoff date has passed
      if (sessionData.cutoffDate) {
        const cutoffDate = this.parseESTDate(sessionData.cutoffDate);
        if (now >= cutoffDate) {
          // Cutoff date has passed, clear session data and require re-selection
          this.clearSessionData();
          return {
            canProceed: false,
            action: 'selectMenu',
            availableMenus,
            message: 'Delivery cutoff date has passed. Please select a new weekly menu and delivery date'
          };
        }
      }
    }

    // Case 1: No weekly menu/delivery date selected
    if (!sessionData.weeklyMenuTitle || !sessionData.deliveryDate) {
      return {
        canProceed: false,
        action: 'selectMenu',
        availableMenus,
        message: 'Please select a weekly menu and delivery date'
      };
    }

    // Case 2: Check if current product has the same weekly menu
    const currentMenu = availableMenus.find(menu => menu.title === sessionData.weeklyMenuTitle);
    if (currentMenu) {
      // Check if delivery date is still valid
      const validDeliveryDate = this.isDeliveryDateValid(currentMenu, sessionData.deliveryDate);
      if (validDeliveryDate) {
        return {
          canProceed: true,
          action: 'proceed',
          selectedMenu: currentMenu,
          selectedDeliveryDate: sessionData.deliveryDate,
          message: 'Compatible with existing weekly menu selection'
        };
      }
    }

    // Case 3: Different weekly menu or invalid delivery date
    return {
      canProceed: false,
      action: 'resolveConflict',
      currentMenuTitle: sessionData.weeklyMenuLabel || sessionData.weeklyMenuTitle,
      currentDeliveryDate: sessionData.deliveryDate,
      availableMenus,
      message: 'Weekly menu conflict detected'
    };
  }

  /**
   * Check if there are items in the cart
   */
  static async hasCartItems() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const cartData = await response.json();
        return cartData.item_count > 0;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking cart items:', error);
      return false;
    }
  }

  /**
   * Clear the cart using Shopify's cart API
   */
  static async clearCart() {
    return new Promise((resolve, reject) => {
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (response.ok) {
          this.clearSessionData();
          location.reload();
          resolve();
        } else {
          reject(new Error('Failed to clear cart'));
        }
      })
      .catch(reject);
    });
  }

  /**
   * Format a delivery date for display
   */
  static formatDeliveryDate(dateString) {
    // Ensure no timezone issues
    const date = new Date(dateString.includes('T') ? dateString : dateString + 'T12:00:00');
    const options = { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Get available delivery dates for a weekly menu
   */
  static getAvailableDeliveryDates(menu) {
    if (!menu || !menu.delivery_dates) {
      return [];
    }

    return menu.delivery_dates
      .filter(dateInfo => this.isDeliveryDateAvailable(dateInfo))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  /**
   * Create a pubsub event for weekly menu changes
   */
  static notifyWeeklyMenuChange(data) {
    const event = new CustomEvent('weeklyMenu:changed', {
      detail: data
    });
    document.dispatchEvent(event);

    // Also use pubsub if available
    if (typeof publish !== 'undefined') {
      publish('weeklyMenu:changed', data);
    }
  }

  /**
   * Listen for weekly menu changes
   */
  static onWeeklyMenuChange(callback) {
    document.addEventListener('weeklyMenu:changed', (event) => {
      callback(event.detail);
    });

    // Also listen to pubsub if available
    if (typeof subscribe !== 'undefined') {
      subscribe('weeklyMenu:changed', callback);
    }
  }
}

// Make it globally available
window.WeeklyMenuUtils = WeeklyMenuUtils;
